import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SELECTORS = {
  open: '[data-c-cart-rodney-open]',
  close: '[data-c-cart-rodney-close]',
  shell: '[data-c-cart-rodney-shell]',
  drawer: '.c-cart-rodney__drawer',
  title: '[data-c-cart-rodney-title]',
  items: '[data-c-cart-rodney-items]',
  subtotal: '[data-c-cart-rodney-subtotal]',
  checkout: '[data-c-cart-rodney-checkout]',
  error: '[data-c-cart-rodney-error]',
  add: '[data-c-cart-rodney-add]',
  offer: '[data-c-offer-cart]',
  offerCard: '[data-c-offer-cart-card]',
  offerToggle: '[data-c-offer-cart-toggle]',
  offerSizes: '[data-c-offer-cart-sizes]',
  offerTrack: '[data-c-offer-cart-track]',
};

/** Movement before a press counts as a drag rather than a click. */
const OFFER_DRAG_THRESHOLD = 5;

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class CCartRodneyDrawer extends HTMLElement {
  /** @type {HTMLElement | null} */
  #lastFocused = null;

  /** @type {Map<string, Promise<number | null>>} */
  #comparePriceRequests = new Map();

  /** @type {Record<string, number | null>} */
  #comparePrices = {};

  /** @type {{startX: number, startScroll: number, moved: boolean, pointerId: number} | null} */
  #offerDrag = null;

  /** Incremented per cart render, so a slow offer lookup cannot overwrite a newer one. */
  #offerRender = 0;

  connectedCallback() {
    this.openButton?.addEventListener('click', this.open);
    this.querySelectorAll(SELECTORS.close).forEach((button) => button.addEventListener('click', this.close));
    this.addEventListener('click', this.#handleClick);
    document.addEventListener('keydown', this.#handleDocumentKeydown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    const offerTrack = this.querySelector(SELECTORS.offerTrack);
    if (offerTrack) {
      window.addEventListener('resize', this.#updateOfferScroll);

      // Drag to scroll the rail. Touch is left alone — the native gesture is
      // better than anything reimplemented here, and hijacking it would break
      // the vertical scroll passthrough.
      offerTrack.addEventListener('pointerdown', this.#handleOfferDragStart);
      offerTrack.addEventListener('pointermove', this.#handleOfferDragMove);
      offerTrack.addEventListener('pointerup', this.#handleOfferDragEnd);
      offerTrack.addEventListener('pointercancel', this.#handleOfferDragEnd);
      offerTrack.addEventListener('dragstart', this.#preventOfferDragImage);
    }

    this.#refreshCart(false);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.open);
    this.querySelectorAll(SELECTORS.close).forEach((button) => button.removeEventListener('click', this.close));
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    const offerTrack = this.querySelector(SELECTORS.offerTrack);
    offerTrack?.removeEventListener('pointerdown', this.#handleOfferDragStart);
    offerTrack?.removeEventListener('pointermove', this.#handleOfferDragMove);
    offerTrack?.removeEventListener('pointerup', this.#handleOfferDragEnd);
    offerTrack?.removeEventListener('pointercancel', this.#handleOfferDragEnd);
    offerTrack?.removeEventListener('dragstart', this.#preventOfferDragImage);
    window.removeEventListener('resize', this.#updateOfferScroll);
  }

  /** Product images are draggable by default, which fights the rail. */
  #preventOfferDragImage = (event) => {
    event.preventDefault();
  };

  #handleOfferDragStart = (event) => {
    if (event.pointerType === 'touch' || event.button !== 0) return;

    const track = event.currentTarget;
    if (track.scrollWidth - track.clientWidth <= 1) return;

    this.#offerDrag = {
      startX: event.clientX,
      startScroll: track.scrollLeft,
      moved: false,
      pointerId: event.pointerId,
    };
  };

  #handleOfferDragMove = (event) => {
    const drag = this.#offerDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const track = event.currentTarget;
    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < OFFER_DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      track.classList.add('is-dragging');
      // Capture late, so a press that turns out to be a click is untouched.
      track.setPointerCapture?.(drag.pointerId);
    }

    track.scrollLeft = drag.startScroll - delta;
    event.preventDefault();
  };

  #handleOfferDragEnd = (event) => {
    const drag = this.#offerDrag;
    if (!drag) return;
    this.#offerDrag = null;

    const track = event.currentTarget;
    track.classList.remove('is-dragging');
    if (track.hasPointerCapture?.(drag.pointerId)) track.releasePointerCapture(drag.pointerId);

    if (!drag.moved) return;

    // A drag that finishes over the + or a size chip must not also press it.
    const swallow = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };
    track.addEventListener('click', swallow, { capture: true, once: true });
    window.setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
  };

  /**
   * The scrollbar itself is the browser's, styled in CSS — nothing here mirrors
   * scroll position, so nothing can drift out of sync with it. This only reports
   * whether the rail can be dragged at all, which is what the grab cursor needs.
   */
  #updateOfferScroll = () => {
    const track = this.querySelector(SELECTORS.offerTrack);
    if (!track) return;
    track.classList.toggle('is-draggable', track.scrollWidth - track.clientWidth > 1);
  }

  get openButton() {
    return this.querySelector(SELECTORS.open);
  }

  get shell() {
    return this.querySelector(SELECTORS.shell);
  }

  get drawer() {
    return this.querySelector(SELECTORS.drawer);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open = () => {
    if (this.isOpen) return;

    this.#lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.shell?.removeAttribute('hidden');
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('c-cart-rodney-lock');

    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.drawer?.focus();
      this.#refreshShippingProtection();
    });

    // Opening reconciles against the live cart. Every in-page path already
    // fires cart:update, so this is normally a no-op — it matters when the cart
    // changed without an event reaching this tab (a second tab, a back-forward
    // restore), where the offer rail would otherwise open showing a hoodie the
    // customer already has.
    this.#refreshCart(false);
  };

  close = () => {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('c-cart-rodney-lock');

    window.setTimeout(() => {
      this.shell?.setAttribute('hidden', '');
      this.#lastFocused?.focus();
    }, 220);
  };

  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    if (this.hasAttribute('auto-open') && event.detail?.data?.source === 'product-form-component') {
      // The second-hoodie offer modal takes the add-to-cart moment when it
      // fires, so the two never open on top of each other. It decides in the
      // capture phase of this same event and marks <html> if it is taking over;
      // the mark is cleared on the next frame, so this only ever suppresses the
      // one add the modal actually claimed.
      if (!document.documentElement.hasAttribute('data-c-offer-modal-claimed')) {
        this.open();
      }
    }

    this.#refreshCart(false);
  };

  #handleDocumentKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.#trapFocus(event);
    }
  };

  #trapFocus(event) {
    const focusable = Array.from(this.drawer?.querySelectorAll(focusableSelector) || []).filter(
      (element) => element instanceof HTMLElement && element.offsetParent !== null
    );

    if (focusable.length === 0) {
      event.preventDefault();
      this.drawer?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const quantityButton = target.closest('[data-c-cart-rodney-quantity]');
    if (quantityButton instanceof HTMLButtonElement) {
      const key = quantityButton.dataset.key;
      const quantity = parseInt(quantityButton.dataset.quantity || '0', 10);
      if (key) this.#changeQuantity(key, quantity);
      return;
    }

    const removeButton = target.closest('[data-c-cart-rodney-remove]');
    if (removeButton instanceof HTMLButtonElement) {
      const key = removeButton.dataset.key;
      if (key) this.#changeQuantity(key, 0);
      return;
    }

    // The offer rail's + opens a size overlay rather than adding blind: the
    // hoodies have sizes, and the design is explicit that nothing enters the
    // cart until one is chosen. The chips inside carry the add hook below, so
    // choosing a size runs the ordinary add path.
    const offerToggle = target.closest(SELECTORS.offerToggle);
    if (offerToggle instanceof HTMLButtonElement) {
      const sizes = offerToggle.parentElement?.querySelector(SELECTORS.offerSizes);
      const opening = sizes?.hidden === true;
      this.#closeOfferSizes();
      if (sizes && opening) {
        sizes.hidden = false;
        offerToggle.setAttribute('aria-expanded', 'true');
        sizes.querySelector('[data-c-cart-rodney-add]')?.focus();
      }
      return;
    }

    const addButton = target.closest(SELECTORS.add);
    if (addButton instanceof HTMLButtonElement) {
      const variantId = addButton.dataset.cCartRodneyAdd;
      if (variantId) this.#addRecommendation(variantId);
    }
  };

  /** Only one size overlay is open at a time, and none survive a re-render. */
  #closeOfferSizes() {
    for (const sizes of this.querySelectorAll(SELECTORS.offerSizes)) sizes.hidden = true;
    for (const toggle of this.querySelectorAll(SELECTORS.offerToggle)) {
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  async #refreshCart(showLoading = true) {
    try {
      if (showLoading) this.#setLoading(true);
      const cart = await this.#request('/cart.js');
      await this.#render(cart);
      this.#hideError();
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setLoading(false);
    }
  }

  async #changeQuantity(key, quantity) {
    try {
      this.#setLoading(true);
      const cart = await this.#request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.#render(cart);
      this.#dispatchCartUpdate(cart, 'c-cart-rodney-change');
      this.#hideError();
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setLoading(false);
    }
  }

  async #addRecommendation(variantId) {
    try {
      this.#setLoading(true);
      const cart = await this.#request('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      const refreshedCart = cart.items ? cart : await this.#request('/cart.js');
      await this.#render(refreshedCart);
      this.#dispatchCartUpdate(refreshedCart, 'c-cart-rodney-recommendation');
      this.#hideError();
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setLoading(false);
    }
  }

  async #request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...options,
    });
    const payload = await response.json();

    if (!response.ok || payload.errors) {
      throw new Error(payload.description || payload.message || payload.errors || 'Cart request failed.');
    }

    return payload;
  }

  async #render(cart) {
    try {
      await this.#renderCart(cart);
    } finally {
      this.#refreshShippingProtection();
    }
  }

  // Navidium mounts into .nvd-mini once on page load, so its price tier has to be
  // recalculated whenever the drawer re-renders against a new subtotal. Guarded
  // because the app script is deferred and may not have defined nvd_init yet.
  #refreshShippingProtection() {
    if (typeof nvd_init !== 'function') return;
    nvd_init();
  }

  async #renderCart(cart) {
    await this.#loadComparePrices(cart.items || []);

    const itemCount = cart.item_count || 0;
    const title = this.querySelector(SELECTORS.title);
    const items = this.querySelector(SELECTORS.items);
    const subtotal = this.querySelector(SELECTORS.subtotal);
    const checkout = this.querySelector(SELECTORS.checkout);

    this.#renderOfferPrompt(cart);

    if (title) title.textContent = `Your Cart (${itemCount})`;

    const tiers = this.querySelector('c-bf-cart-tiers');
    if (tiers) customElements.whenDefined('c-bf-cart-tiers').then(() => tiers.update(cart));
    if (subtotal) subtotal.textContent = this.#formatMoney(cart.total_price || 0, cart.currency);
    if (checkout instanceof HTMLButtonElement) {
      checkout.dataset.cartEmpty = itemCount === 0 ? 'true' : 'false';
      checkout.disabled = itemCount === 0 || this.classList.contains('is-loading');
    }

    if (!items) return;

    if (itemCount === 0) {
      items.innerHTML = `
        <div class="c-cart-rodney__empty">
          <div>
            <h3>Your Cart Is Empty</h3>
            <a href="/collections/all">Continue Shopping</a>
          </div>
        </div>
      `;
      return;
    }

    items.innerHTML = cart.items.map((item) => this.#renderItem(item, cart.currency)).join('');
  }

  /**
   * The cart holds units of each offer, keyed by strand. Read from a section
   * rather than /cart.js, which carries no collection data. Null on failure,
   * which keeps the prompt hidden rather than guessing.
   *
   * @returns {Promise<Record<string, number> | null>}
   */
  async #fetchOfferUnits() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}?section_id=c-offer-cart-strands`);
      if (!response.ok) return null;
      const html = await response.text();
      const counts = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-c-offer-cart-strands]');
      if (!(counts instanceof HTMLElement)) return null;
      return {
        second_item: Number(counts.dataset.secondItem) || 0,
        tshirt: Number(counts.dataset.tshirt) || 0,
      };
    } catch {
      return null;
    }
  }

  /**
   * The offer prompt shows while the cart holds exactly one item of an offer:
   * at zero there is nothing to pair with, at two the offer is already taken.
   * When both offers qualify, the hoodie offer wins.
   *
   * @param {{items?: Array<{product_id?: number, quantity?: number}>}} cart
   */
  async #renderOfferPrompt(cart) {
    const offer = this.querySelector(SELECTORS.offer);
    if (!(offer instanceof HTMLElement)) return;

    // A size was chosen, or the cart changed under it — either way the overlay
    // has served its purpose.
    this.#closeOfferSizes();

    const render = ++this.#offerRender;
    const units = await this.#fetchOfferUnits();
    // A later cart change has already started its own render.
    if (render !== this.#offerRender) return;

    const cardCounts = {
      second_item: Number(offer.dataset.cardsSecondItem) || 0,
      tshirt: Number(offer.dataset.cardsTshirt) || 0,
    };
    const strand = ['second_item', 'tshirt'].find(
      (key) => units?.[key] === 1 && cardCounts[key] > 1
    );

    offer.hidden = !strand;
    if (!strand) return;

    for (const copy of offer.querySelectorAll('[data-c-offer-cart-copy]')) {
      if (copy instanceof HTMLElement) copy.hidden = copy.dataset.cOfferCartCopy !== strand;
    }

    const inCart = new Set((cart.items ?? []).map((item) => String(item.product_id ?? '')));
    const maxCards = strand === 'tshirt' ? Number(offer.dataset.maxCardsTshirt) || Infinity : Infinity;

    // Never offer an item that is already in the cart as the "second" one.
    let visibleCards = 0;
    for (const card of offer.querySelectorAll(SELECTORS.offerCard)) {
      if (!(card instanceof HTMLElement)) continue;
      card.hidden =
        card.dataset.cOfferCartStrand !== strand ||
        inCart.has(String(card.dataset.productId)) ||
        visibleCards >= maxCards;
      if (!card.hidden) visibleCards += 1;
    }

    // Hiding every card leaves the heading and the discount pill sitting above
    // an empty row, which reads as a broken offer rather than an absent one.
    if (visibleCards === 0) {
      offer.hidden = true;
      return;
    }

    // Hiding a card changes the scroll width, so the indicator is re-measured
    // after layout rather than against the widths it had a moment ago.
    requestAnimationFrame(this.#updateOfferScroll);
  }

  #renderItem(item, currency) {
    const image = item.featured_image?.url || item.image || '';
    const imageAlt = item.featured_image?.alt || item.product_title || item.title || '';
    const comparePrice = this.#getComparePrice(item);
    const currentPrice = item.final_price ?? item.price ?? 0;
    const options = this.#renderOptions(item);

    return `
      <article class="c-cart-rodney__item" data-key="${this.#escapeAttribute(item.key)}">
        <a class="c-cart-rodney__item-image" href="${this.#escapeAttribute(item.url || '#')}">
          ${image ? `<img src="${this.#escapeAttribute(image)}" alt="${this.#escapeAttribute(imageAlt)}" loading="lazy">` : ''}
        </a>
        <div class="c-cart-rodney__item-main">
          <div>
            <h3 class="c-cart-rodney__item-title">${this.#escapeHtml(item.product_title || item.title)}</h3>
            ${options ? `<p class="c-cart-rodney__item-options">${options}</p>` : ''}
          </div>
          <button
            class="c-cart-rodney__remove button-unstyled"
            type="button"
            data-c-cart-rodney-remove
            data-key="${this.#escapeAttribute(item.key)}"
            aria-label="Remove ${this.#escapeAttribute(item.product_title || item.title)}"
          >
            ${this.#removeIcon()}
          </button>
          <div class="c-cart-rodney__item-bottom">
            <div class="c-cart-rodney__quantity" aria-label="Quantity for ${this.#escapeAttribute(item.product_title || item.title)}">
              <button
                type="button"
                name="minus"
                data-c-cart-rodney-quantity
                data-key="${this.#escapeAttribute(item.key)}"
                data-quantity="${Math.max((item.quantity || 1) - 1, 0)}"
                aria-label="Decrease quantity"
              >${this.#minusIcon()}</button>
              <span>${item.quantity || 0}</span>
              <button
                type="button"
                name="plus"
                data-c-cart-rodney-quantity
                data-key="${this.#escapeAttribute(item.key)}"
                data-quantity="${(item.quantity || 0) + 1}"
                aria-label="Increase quantity"
              >${this.#plusIcon()}</button>
            </div>
            <div class="c-cart-rodney__price">
              ${
                comparePrice && comparePrice > currentPrice
                  ? `<s class="c-cart-rodney__compare-price">${this.#formatMoney(comparePrice, currency)}</s>`
                  : ''
              }
              <strong class="c-cart-rodney__current-price">${this.#formatMoney(currentPrice, currency)}</strong>
            </div>
          </div>
        </div>
      </article>
    `;
  }

  #renderOptions(item) {
    const values = [];

    if (Array.isArray(item.options_with_values)) {
      item.options_with_values.forEach((option) => {
        if (option?.value && option.value !== 'Default Title') values.push(option.value);
      });
    } else if (Array.isArray(item.variant_options)) {
      item.variant_options.forEach((option) => {
        if (option && option !== 'Default Title') values.push(option);
      });
    }

    return values.map((value) => `<span>${this.#escapeHtml(value)}</span>`).join('');
  }

  async #loadComparePrices(items) {
    await Promise.all(
      items.map(async (item) => {
        const cacheKey = String(item.variant_id);
        if (Object.prototype.hasOwnProperty.call(this.#comparePrices, cacheKey)) return;
        if (!item.handle) {
          this.#comparePrices[cacheKey] = null;
          return;
        }

        if (!this.#comparePriceRequests.has(cacheKey)) {
          this.#comparePriceRequests.set(cacheKey, this.#fetchComparePrice(item.handle, item.variant_id));
        }

        this.#comparePrices[cacheKey] = await this.#comparePriceRequests.get(cacheKey);
      })
    );
  }

  async #fetchComparePrice(handle, variantId) {
    try {
      const product = await this.#request(`/products/${handle}.js`);
      const variant = product.variants?.find((candidate) => String(candidate.id) === String(variantId));
      return variant?.compare_at_price || null;
    } catch (_) {
      return null;
    }
  }

  #getComparePrice(item) {
    const variantComparePrice = this.#comparePrices[String(item.variant_id)];
    if (variantComparePrice) return variantComparePrice;
    if (item.original_price && item.final_price && item.original_price > item.final_price) return item.original_price;
    return null;
  }

  #dispatchCartUpdate(cart, source) {
    this.dispatchEvent(
      new CartUpdateEvent(cart, 'c-cart-rodney-drawer', {
        itemCount: cart.item_count || 0,
        source,
      })
    );
  }

  #setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.querySelectorAll('button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.matches(SELECTORS.open) || button.matches(SELECTORS.close)) return;
      if (button.matches(SELECTORS.checkout)) {
        button.disabled = isLoading || button.dataset.cartEmpty === 'true';
        return;
      }
      button.disabled = isLoading;
    });
  }

  #showError(error) {
    const errorElement = this.querySelector(SELECTORS.error);
    if (!errorElement) return;
    errorElement.textContent = error instanceof Error ? error.message : 'Unable to update the cart. Please try again.';
    errorElement.removeAttribute('hidden');
  }

  #hideError() {
    const errorElement = this.querySelector(SELECTORS.error);
    if (!errorElement) return;
    errorElement.textContent = '';
    errorElement.setAttribute('hidden', '');
  }

  #formatMoney(cents, currency) {
    const activeCurrency = currency || window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activeCurrency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
    }
  }

  #escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  #escapeAttribute(value = '') {
    return this.#escapeHtml(value);
  }

  #minusIcon() {
    return '<svg aria-hidden="true" viewBox="0 0 20 20" focusable="false"><path d="M4 10h12" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  }

  #plusIcon() {
    return '<svg aria-hidden="true" viewBox="0 0 20 20" focusable="false"><path d="M10 4v12M4 10h12" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  }

  #removeIcon() {
    return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M4 7H20" stroke="black" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/><path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" stroke="black" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/><path d="M9 7V4C9 3.73478 9.10536 3.48043 9.29289 3.29289C9.48043 3.10536 9.73478 3 10 3H14C14.2652 3 14.5196 3.10536 14.7071 3.29289C14.8946 3.48043 15 3.73478 15 4V7" stroke="black" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/><path d="M10 12L14 16M14 12L10 16" stroke="black" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/></svg>';
  }
}

if (!customElements.get('c-cart-rodney-drawer')) {
  customElements.define('c-cart-rodney-drawer', CCartRodneyDrawer);
}
